/**
 * 0-product-gallery-1.js
 * Product gallery with variant-based image filtering.
 *
 * Alt text convention:  "Color | Descriptive alt text"
 * Images whose alt text prefix matches the selected variant's color are shown.
 * Images with no color prefix are hidden (not treated as universal).
 */

if (!customElements.get('variant-image-gallery')) {
  class VariantImageGallery extends HTMLElement {
    constructor() {
      super();
      this.variantColors = {};
      this.currentColor = null;
      this.allMediaItems = [];
    }

    connectedCallback() {
      /* Parse variant → color map (shared by both modes) */
      const jsonEl = this.querySelector('[data-variant-colors]');
      if (jsonEl) {
        try {
          this.variantColors = JSON.parse(jsonEl.textContent);
        } catch (e) { console.error('[variant-image-gallery] Failed to parse variant colors JSON:', e); }
      }

      /* Detect stacked mode — stacked HTML is shown on desktop, thumbnail on mobile */
      this.isStacked = !!this.querySelector('[data-stacked-item]');
      if (this.isStacked) {
        this.stackedItems = this.querySelectorAll('[data-stacked-item]');
      }

      /* Thumbnail mode setup (always — used on mobile even in stacked mode) */
      this.mainImage = this.querySelector('[data-main-image]');
      this.mainImageWrapper = this.querySelector('[data-main-image-wrapper]');
      this.thumbnails = this.querySelectorAll('[data-thumbnail]');
      this.thumbnailItems = this.querySelectorAll('[data-thumbnail-item]');

      const mediaEl = this.querySelector('[data-media-list]');
      if (mediaEl) {
        try {
          this.allMediaItems = JSON.parse(mediaEl.textContent);
        } catch (e) { console.error('[variant-image-gallery] Failed to parse media list JSON:', e); }
      }

      /* Inject spinner element */
      if (this.mainImageWrapper) {
        this.spinner = document.createElement('div');
        this.spinner.className = 'fresh-gallery__spinner';
        this.mainImageWrapper.appendChild(this.spinner);
      }

      /* Sync thumbnail column height to main image height so overflow scroll works.
         Double-nested rAF ensures layout has painted. Also observe resize and
         window load to catch cached images that won't fire a load event. */
      requestAnimationFrame(() => requestAnimationFrame(() => this.syncThumbHeight()));
      if (this.mainImage) {
        this.mainImage.addEventListener('load', () => this.syncThumbHeight(), { once: true });
      }
      window.addEventListener('load', () => {
        this.syncThumbHeight();
        /* Re-apply filtering after all third-party scripts have run.
           Affiliate/tracking apps (e.g. Awin) process URL params like
           `selected_variant` on load, which can trigger AJAX section
           re-renders that reset thumbnail display styles. Re-calling
           filterByColor here restores the correct filtered state. */
        if (this.currentColor) {
          this.filterByColor(this.currentColor);
        } else {
          this.setInitialState();
        }
      }, { once: true });
      if (window.ResizeObserver && this.mainImageWrapper) {
        const ro = new ResizeObserver(() => this.syncThumbHeight());
        ro.observe(this.mainImageWrapper);
      }

      this.bindThumbnails();
      this.bindVariantChange();
      this.bindSwipe();
      this.setInitialState();
    }

    syncThumbHeight() {
      const thumbsList = this.querySelector('.fresh-gallery__thumbs');
      if (!thumbsList || !this.mainImageWrapper) return;
      const h = this.mainImageWrapper.offsetHeight;
      if (h > 0) thumbsList.style.maxHeight = h + 'px';
    }

    bindThumbnails() {
      this.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          const mediaId = thumb.getAttribute('data-media-id');
          this.setMainImage(mediaId);
          this.setActiveThumbnail(thumb);
        });

        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            thumb.click();
          }
        });
      });
    }

    bindSwipe() {
      if (!this.mainImageWrapper) return;
      let touchStartX = 0;
      let touchStartY = 0;

      this.mainImageWrapper.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].clientX;
        touchStartY = e.changedTouches[0].clientY;
      }, { passive: true });

      this.mainImageWrapper.addEventListener('touchend', (e) => {
        const deltaX = e.changedTouches[0].clientX - touchStartX;
        const deltaY = e.changedTouches[0].clientY - touchStartY;

        /* Ignore if primarily a vertical scroll */
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;

        const visibleItems = Array.from(this.thumbnailItems).filter(
          (item) => item.style.display !== 'none'
        );
        if (visibleItems.length < 2) return;

        const activeThumb = Array.from(this.thumbnails).find((t) => t.classList.contains('is-active'));
        const activeItem = activeThumb ? activeThumb.closest('[data-thumbnail-item]') : null;
        let currentIndex = visibleItems.indexOf(activeItem);
        if (currentIndex === -1) currentIndex = 0;

        const nextIndex = deltaX < 0
          ? (currentIndex + 1) % visibleItems.length          /* swipe left → next */
          : (currentIndex - 1 + visibleItems.length) % visibleItems.length; /* swipe right → prev */

        const nextItem = visibleItems[nextIndex];
        const nextThumb = nextItem.querySelector('[data-thumbnail]');
        const nextMediaId = nextItem.getAttribute('data-media-id');

        if (nextMediaId) this.setMainImage(nextMediaId);
        if (nextThumb) {
          this.setActiveThumbnail(nextThumb);
          nextThumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }, { passive: true });
    }

    getVariantIdFromForm() {
      const productInfo = this.closest('product-info');
      if (productInfo) {
        const input = productInfo.querySelector('input[name="id"]');
        if (input) return input.value;
      }
      const form = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (form) return form.value;
      return null;
    }

    getVariantIdFromUrl() {
      return new URLSearchParams(window.location.search).get('variant');
    }

    bindVariantChange() {
      /* Read e.target.value directly — no URL timing dependency, no option-name parsing.
         We only act when the changed value is a known color, which filters out changes
         to other options without needing to inspect the radio name or picker structure. */
      const knownColors = new Set(
        Object.values(this.variantColors).map((c) => c.toLowerCase())
      );

      document.addEventListener('change', (e) => {
        const pickerRoot = e.target.closest('[data-hume-picker-root], variant-selects');
        if (!pickerRoot) return;
        const productInfo = this.closest('product-info');
        if (productInfo && !productInfo.contains(pickerRoot)) return;
        if (!knownColors.has((e.target.value || '').toLowerCase())) return;

        this.currentColor = e.target.value;
        this.filterByColor(e.target.value);
      });

      /* Fallback: handle browser back/forward navigation via URL */
      window.addEventListener('popstate', () => this.updateColorFromVariant());
    }

    setInitialState() {
      this.updateColorFromVariant();
    }

    updateColorFromVariant() {
      /* URL is updated synchronously by Dawn's replaceState on variant change;
         form input[name="id"] is updated async via AJAX section response.
         Always prefer the URL so we never read a stale form value. */
      const variantId = this.getVariantIdFromUrl() || this.getVariantIdFromForm();
      let color = variantId ? (this.variantColors[variantId] || null) : null;

      if (!color) {
        /* variantId missing or not in map (e.g. an affiliate app stripped ?variant=
           from the URL, or the app triggered an AJAX re-render with a different
           context). Fall back to whichever radio is currently checked. */
        const productInfo = this.closest('product-info');
        const root = productInfo || document;
        const checked = root.querySelector(
          '[data-hume-picker-root] input[type="radio"]:checked, variant-selects input[type="radio"]:checked'
        );
        if (checked && checked.value) {
          const knownColors = new Set(Object.values(this.variantColors).map((c) => c.toLowerCase()));
          if (knownColors.has(checked.value.toLowerCase())) color = checked.value;
        }
      }

      if (!color) {
        /* Last resort: use the first mapped variant's color. */
        const firstKey = Object.keys(this.variantColors)[0];
        if (firstKey) color = this.variantColors[firstKey];
      }

      if (color && color !== this.currentColor) {
        this.currentColor = color;
        this.filterByColor(color);
      }
    }

    filterByColor(color) {
      /* Re-query live DOM nodes — AJAX section re-renders can replace our children
         while the <variant-image-gallery> element itself stays in the DOM, leaving
         the cached NodeLists pointing at detached nodes that are no longer visible. */
      this.thumbnailItems = this.querySelectorAll('[data-thumbnail-item]');
      this.thumbnails = this.querySelectorAll('[data-thumbnail]');
      if (this.isStacked) this.stackedItems = this.querySelectorAll('[data-stacked-item]');

      /* Always filter the thumbnail layout (used on mobile even in stacked mode) */
      const colorLower = color.toLowerCase().trim();
      let firstVisibleThumb = null;
      let firstVisibleMediaId = null;

      this.thumbnailItems.forEach((item) => {
        const itemColor = (item.getAttribute('data-color') || '').toLowerCase().trim();
        const isMatch = itemColor === colorLower;
        item.style.display = isMatch ? '' : 'none';
        item.setAttribute('aria-hidden', isMatch ? 'false' : 'true');

        if (isMatch && !firstVisibleThumb) {
          firstVisibleThumb = item.querySelector('[data-thumbnail]');
          firstVisibleMediaId = item.getAttribute('data-media-id');
        }
      });

      if (firstVisibleMediaId) this.setMainImage(firstVisibleMediaId);
      if (firstVisibleThumb) this.setActiveThumbnail(firstVisibleThumb);

      /* Additionally filter the stacked layout (desktop) */
      if (this.isStacked) this._filterStacked(color);

      const status = this.querySelector('[data-gallery-status]');
      if (status) status.textContent = 'Showing images for ' + color;
    }

    setMainImage(mediaId) {
      const media = this.allMediaItems.find((m) => String(m.id) === String(mediaId));
      if (!media || !this.mainImage) return;

      /* Fade out current image and show spinner */
      this.mainImage.style.opacity = '0';
      if (this.spinner) this.spinner.classList.add('is-loading');

      /* Preload the new image so the swap is instant */
      const preload = new Image();
      preload.onload = () => {
        this.mainImage.src = media.src;
        this.mainImage.srcset = media.srcset;
        this.mainImage.alt = media.alt;
        this.mainImage.width = media.width;
        this.mainImage.height = media.height;
        if (this.mainImageWrapper) {
          const ratio = media.width && media.height ? (media.height / media.width * 100) : 100;
          this.mainImageWrapper.style.setProperty('--aspect-ratio', ratio + '%');
        }
        if (this.spinner) this.spinner.classList.remove('is-loading');
        this.mainImage.style.opacity = '1';
        this.syncThumbHeight();
      };
      preload.onerror = () => {
        if (this.spinner) this.spinner.classList.remove('is-loading');
        this.mainImage.style.opacity = '1';
      };
      preload.src = media.src;
    }

    setActiveThumbnail(activeThumb) {
      this.thumbnails.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-current', 'false');
      });
      activeThumb.classList.add('is-active');
      activeThumb.setAttribute('aria-current', 'true');
    }

    _filterStacked(color) {
      const colorLower = color.toLowerCase().trim();
      const visibleItems = [];

      /* Reset all items */
      this.stackedItems.forEach((item) => {
        item.classList.remove('hume-stacked-hero', 'hume-stacked-last-full');
        item.style.display = 'none';
        item.setAttribute('aria-hidden', 'true');
      });

      /* Collect visible matches */
      this.stackedItems.forEach((item) => {
        const itemColor = (item.getAttribute('data-color') || '').toLowerCase().trim();
        if (itemColor === colorLower) {
          item.style.display = '';
          item.setAttribute('aria-hidden', 'false');
          visibleItems.push(item);
        }
      });

      /* First item is the hero — full width */
      if (visibleItems.length > 0) {
        visibleItems[0].classList.add('hume-stacked-hero');
      }

      /* Remaining items: if odd count, the last one is alone → span full width */
      const remaining = visibleItems.length - 1;
      if (remaining > 0 && remaining % 2 !== 0) {
        visibleItems[visibleItems.length - 1].classList.add('hume-stacked-last-full');
      }

      const status = this.querySelector('[data-gallery-status]');
      if (status) status.textContent = 'Showing images for ' + color;
    }
  }

  customElements.define('variant-image-gallery', VariantImageGallery);
}
